import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { createInvoice, NewInvoiceData } from '../../../services/invoiceService';
import { getServiceRequestsForSelection, ServiceRequestSelectionData } from '../../../services/serviceRequestService';
import { getSubscriptionsForSelection, SubscriptionSelectionData } from '../../../services/subscriptionService';
import { InvoiceType } from '../../../types/enums';
import formStyles from '../trainees/TraineeForm.module.css';

const AddInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // حالات لتخزين القوائم المنسدلة
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestSelectionData[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionSelectionData[]>([]);

  const { register, handleSubmit, formState: { errors }, control, setValue } = useForm<NewInvoiceData>();

  // مراقبة حقل "النوع" وحقل "المعرف المرتبط"
  const selectedType = useWatch({ control, name: 'type' });
  const selectedRelatedId = useWatch({ control, name: 'related_id' });

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [requests, subs] = await Promise.all([
          getServiceRequestsForSelection(),
          getSubscriptionsForSelection(),
        ]);
        setServiceRequests(requests);
        setSubscriptions(subs);
      } catch (error) {
        toast.error('فشل في جلب بيانات الطلبات والاشتراكات.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // تحديث المبلغ تلقائيًا عند تغيير الاختيار
  useEffect(() => {
    if (!selectedRelatedId) {
        setValue('amount', 0);
        return;
    };

    let selectedItem;
    if (selectedType === InvoiceType.SERVICE) {
      selectedItem = serviceRequests.find(r => r.id === Number(selectedRelatedId));
    } else if (selectedType === InvoiceType.SUBSCRIPTION) {
      selectedItem = subscriptions.find(s => s.id === Number(selectedRelatedId));
    }

    if (selectedItem) {
      setValue('amount', selectedItem.fee);
    }
  }, [selectedRelatedId, selectedType, serviceRequests, subscriptions, setValue]);

  const onSubmit: SubmitHandler<NewInvoiceData> = (data) => {
    setIsSubmitting(true);
    const promise = createInvoice(data);
    toast.promise(promise, {
      loading: 'جاري إنشاء الفاتورة...',
      success: () => {
        navigate('/admin/invoices/list');
        return 'تم إنشاء الفاتورة بنجاح!';
      },
      error: (err) => {
        setIsSubmitting(false);
        return err.response?.data?.message || 'فشل في إنشاء الفاتورة.';
      },
    });
  };

  if (isLoading) {
    return <div>جاري تحميل البيانات...</div>;
  }

  return (
    <div className={formStyles.pageContainer}>
      <div className={formStyles.formContainer}>
        <h2 className={formStyles.title}>إضافة فاتورة جديدة</h2>
        <form onSubmit={handleSubmit(onSubmit)} className={formStyles.form}>
          <div className={formStyles.formField}>
            <label htmlFor="type">نوع الفاتورة</label>
            <select id="type" {...register('type', { required: 'النوع مطلوب' })} className={formStyles.input}>
              <option value="">-- اختر النوع --</option>
              <option value={InvoiceType.SERVICE}>خدمة</option>
              <option value={InvoiceType.SUBSCRIPTION}>اشتراك</option>
            </select>
            {errors.type && <p className={formStyles.error}>{errors.type.message}</p>}
          </div>

          {/* عرض القائمة المنسدلة بناءً على النوع المختار */}
          {selectedType === InvoiceType.SERVICE && (
            <div className={formStyles.formField}>
              <label htmlFor="related_id">اختر طلب الخدمة</label>
              <select id="related_id" {...register('related_id', { required: 'يجب اختيار طلب', valueAsNumber: true })} className={formStyles.input}>
                <option value="">-- اختر طلبًا --</option>
                {serviceRequests.map(req => (
                  <option key={req.id} value={req.id}>{req.displayText}</option>
                ))}
              </select>
              {errors.related_id && <p className={formStyles.error}>{errors.related_id.message}</p>}
            </div>
          )}

          {selectedType === InvoiceType.SUBSCRIPTION && (
            <div className={formStyles.formField}>
              <label htmlFor="related_id">اختر الاشتراك</label>
              <select id="related_id" {...register('related_id', { required: 'يجب اختيار اشتراك', valueAsNumber: true })} className={formStyles.input}>
                <option value="">-- اختر اشتراكًا --</option>
                {subscriptions.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.displayText}</option>
                ))}
              </select>
              {errors.related_id && <p className={formStyles.error}>{errors.related_id.message}</p>}
            </div>
          )}

          <div className={formStyles.formField}>
            <label htmlFor="amount">المبلغ</label>
            <input id="amount" type="number" step="0.01" {...register('amount', { required: 'المبلغ مطلوب', valueAsNumber: true, min: { value: 0.01, message: 'المبلغ يجب أن يكون أكبر من صفر' } })} className={formStyles.input} readOnly />
            {errors.amount && <p className={formStyles.error}>{errors.amount.message}</p>}
            <small>يتم تحديث المبلغ تلقائيًا عند اختيار عنصر.</small>
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="due_date">تاريخ الاستحقاق</label>
            <input id="due_date" type="date" {...register('due_date', { required: 'تاريخ الاستحقاق مطلوب' })} className={formStyles.input} />
            {errors.due_date && <p className={formStyles.error}>{errors.due_date.message}</p>}
          </div>

          <div className={`${formStyles.buttonContainer} ${formStyles.fullWidth}`}>
            <Link to="/admin/invoices/list" className={formStyles.cancelButton}>إلغاء</Link>
            <button type="submit" disabled={isSubmitting || !selectedType} className={formStyles.submitButton}>
              {isSubmitting ? 'جاري الإنشاء...' : 'إنشاء الفاتورة'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInvoicePage;
